<template>
  <div class="property-detail-wrapper">
    <!-- Loading State -->
    <div v-if="!apartment" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Cargando información...</p>
    </div>

    <!-- Property Detail Content -->
    <div class="property-detail fade-in" v-else>
      <!-- Progress Indicator -->
      <BuyProgress
        v-if="['default', 'redondo'].includes(template)"
        :apartment="apartment"
        :country="country"
      />

      <!-- Photo Gallery -->
      <div class="gallery-section" v-if="photos && photos.length > 0">
        <div class="gallery-main" @click="showPhoto">
          <div
            class="gallery-image"
            :style="{
              backgroundImage:
                'url(' + getPhotoUrl(photos[carouselPosition]) + ')',
            }"
          >
            <div class="gallery-overlay">
              <div class="gallery-nav">
                <button
                  class="gallery-btn prev"
                  @click.stop="prevPhoto"
                  v-if="photos.length > 1"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  class="gallery-btn next"
                  @click.stop="nextPhoto"
                  v-if="photos.length > 1"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div class="gallery-counter">
                {{ carouselPosition + 1 }} / {{ photos.length }}
              </div>
              <div class="gallery-hint">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 3H21V9"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9 21H3V15"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M21 3L14 10"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3 21L10 14"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Click para ampliar</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Photo Thumbnails -->
        <div class="gallery-thumbs" v-if="photos.length > 1">
          <button
            v-for="(photo, index) in photos.slice(0, 5)"
            :key="index"
            class="thumb-btn"
            :class="{ active: carouselPosition === index }"
            @click="carouselPosition = index"
            :style="{ backgroundImage: 'url(' + getPhotoUrl(photo) + ')' }"
          >
            <span v-if="index === 4 && photos.length > 5" class="thumb-more">
              +{{ photos.length - 5 }}
            </span>
          </button>
        </div>

        <!-- Fullscreen Modal -->
        <b-modal
          id="modal-photo"
          size="xl"
          centered
          hide-header
          body-bg-variant="dark"
          content-class="gallery-modal"
          hide-footer
        >
          <div class="modal-gallery">
            <img :src="getPhotoUrl(photos[setedPhoto])" alt="Foto propiedad" />
            <div class="modal-nav">
              <button
                class="modal-btn"
                @click="
                  setedPhoto = (setedPhoto - 1 + photos.length) % photos.length
                "
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <span class="modal-counter"
                >{{ setedPhoto + 1 }} / {{ photos.length }}</span
              >
              <button
                class="modal-btn"
                @click="setedPhoto = (setedPhoto + 1) % photos.length"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </b-modal>
      </div>

      <!-- Property Stats -->
      <div class="stats-section" v-if="!hideData(apartment.asset_type)">
        <div class="stats-grid">
          <div
            class="stat-card"
            v-if="apartment.bedrooms"
            style="--card-index: 0"
          >
            <div class="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path d="M21 13H3" stroke="currentColor" stroke-width="1.5" />
                <path
                  d="M3 10C3 8.89543 3.89543 8 5 8H8C9.10457 8 10 8.89543 10 10V13H3V10Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M14 10C14 8.89543 14.8954 8 16 8H19C20.1046 8 21 8.89543 21 10V13H14V10Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
              </svg>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ apartment.bedrooms }}</span>
              <span class="stat-label"
                >Dormitorio{{ apartment.bedrooms > 1 ? "s" : "" }}</span
              >
            </div>
          </div>

          <div
            class="stat-card"
            v-if="apartment.bathrooms"
            style="--card-index: 1"
          >
            <div class="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 12H20V16C20 18.2091 18.2091 20 16 20H8C5.79086 20 4 18.2091 4 16V12Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M6 12V5C6 3.89543 6.89543 3 8 3H9C10.1046 3 11 3.89543 11 5V6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ apartment.bathrooms }}</span>
              <span class="stat-label"
                >Baño{{ apartment.bathrooms > 1 ? "s" : "" }}</span
              >
            </div>
          </div>

          <div
            class="stat-card"
            v-if="apartment.parking_lots"
            style="--card-index: 2"
          >
            <div class="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="8"
                  width="18"
                  height="12"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M7 15H9"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M15 15H17"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M5 8L7 4H17L19 8"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="stat-info">
              <span class="stat-value"
                >{{ apartment.parking_lots }}
                <small style="font-size: 60%">
                  *{{ apartment.parking_lots_data }}</small
                ></span
              >
              <span class="stat-label"
                >Estacionamiento{{ apartment.parking_lots > 1 ? "s" : "" }}
              </span>
            </div>
          </div>

          <div class="stat-card" v-if="apartment.cellars">
            <div class="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 21H21"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M5 21V7L12 3L19 7V21"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 21V15H15V21"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="stat-info">
              <span class="stat-value"
                >{{ apartment.cellars }}
                <small style="font-size: 60%"
                  >*{{ apartment.cellars_data }}</small
                ></span
              >
              <span class="stat-label"
                >Bodega{{ apartment.cellars > 1 ? "s" : "" }}</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Surface Areas -->
      <div class="areas-section content-section" style="--section-index: 1">
        <h3 class="section-title">Superficies</h3>
        <div class="areas-grid">
          <div
            class="area-card"
            v-if="
              country == 1 &&
              apartment.useful_surface &&
              !hideData(apartment.asset_type)
            "
          >
            <span class="area-value"
              >{{ formatNumber(apartment.useful_surface) }} m²*</span
            >
            <span class="area-label">Superficie Útil</span>
          </div>
          <div
            class="area-card"
            v-if="
              country == 0 &&
              apartment.covered_surface &&
              !hideData(apartment.asset_type)
            "
          >
            <span class="area-value"
              >{{ formatNumber(apartment.covered_surface) }} m²*</span
            >
            <span class="area-label">Área Techada</span>
          </div>
          <div
            class="area-card"
            v-if="country == 1 && !hideData(apartment.asset_type)"
          >
            <span
              class="area-value"
              v-if="['CASA', 'LOTE'].includes(apartment.asset_type)"
              >{{ formatNumber(apartment.terrain_surface) }} m²*</span
            >
            <span class="area-value" v-else
              >{{ formatNumber(apartment.terrace_surface) }} m²*</span
            >
            <span
              class="area-label"
              v-if="['CASA', 'LOTE'].includes(apartment.asset_type)"
              >Superficie Terreno</span
            >
            <span class="area-label" v-else>Superficie Terraza</span>
          </div>
          <div
            class="area-card"
            v-if="
              country == 0 &&
              apartment.free_surface &&
              !hideData(apartment.asset_type)
            "
          >
            <span class="area-value"
              >{{ formatNumber(apartment.free_surface) }} m²*</span
            >
            <span class="area-label">Área Libre</span>
          </div>
          <div class="area-card" v-if="apartment.total_surface">
            <span class="area-value"
              >{{ formatNumber(apartment.total_surface) }} m²*</span
            >
            <span class="area-label">Superficie Total</span>
          </div>
        </div>
        <p
          class="full-width small"
          style="font-size: 0.7em; font-style: italic; margin-top: 10px"
        >
          *Tamaños aproximados
        </p>
      </div>

      <!-- Construction Progress -->
      <div
        ref="progressSection"
        class="progress-section"
        :class="{
          'inverted-colors': isLightBackground,
          'animate-in': progressAnimated,
        }"
        v-if="apartment.progress && apartment.progress >= 0"
      >
        <div class="progress-header">
          <h3 class="section-title">Avance de Obra</h3>
          <span class="progress-percent"
            >{{ progressAnimated ? apartment.progress : 0 }}%</span
          >
        </div>
        <div class="progress-bar-container">
          <div
            class="progress-bar-fill"
            :style="{
              width: (progressAnimated ? apartment.progress : 0) + '%',
            }"
          ></div>
        </div>
      </div>

      <!-- Key Dates -->
      <div
        class="dates-section content-section"
        style="--section-index: 2"
        v-if="assetDatesLoading || visibleAssetDates.length > 0"
      >
        <div class="dates-panel" :class="{ loading: assetDatesLoading }">
          <div class="dates-header">
            <div>
              <span class="dates-eyebrow">Agenda de la propiedad</span>
              <h3 class="section-title">Fechas importantes</h3>
            </div>
            <div class="dates-calendar-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="16"
                  rx="3"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
                <path
                  d="M8 3V7"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
                <path
                  d="M16 3V7"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
                <path
                  d="M3 10H21"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
              </svg>
            </div>
          </div>

          <div v-if="assetDatesLoading" class="dates-skeleton-list">
            <div class="dates-skeleton-item" v-for="index in 3" :key="index">
              <span class="dates-skeleton-dot"></span>
              <div class="dates-skeleton-copy">
                <span></span>
                <strong></strong>
              </div>
            </div>
          </div>

          <div class="dates-list" v-else>
            <div
              v-for="(item, index) in visibleAssetDates"
              :key="item.key"
              class="date-row"
              :class="{ main: item.key === 'delivery_date' }"
              :style="{ '--date-index': index }"
            >
              <div class="date-marker" aria-hidden="true">
                <svg
                  v-if="item.key === 'delivery_date'"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M20 7L10 17L5 12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span v-else></span>
              </div>
              <div class="date-content">
                <span class="date-label">{{ item.label }}</span>
                <strong>{{ formatDate(item.value) }}</strong>
                <small>{{ item.description }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Project Features -->
      <div
        class="features-section content-section"
        style="--section-index: 3"
        v-if="features && features.length > 0"
      >
        <h3 class="section-title">Características del Proyecto</h3>
        <div class="features-grid">
          <div
            class="feature-card"
            v-for="(feature, key) in features"
            :key="key"
          >
            <div class="feature-icon">
              <img
                v-if="hasImage(feature)"
                :src="feature.urlLogoCaracteristica"
                alt=""
              />
              <font-awesome-icon
                v-else
                :icon="['fal', setIcon(feature)]"
                class="feature-fa-icon"
              />
            </div>
            <span class="feature-text">
              {{
                feature.mostrarCategoriaTitulo
                  ? feature.categoria
                  : feature.descripcion
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Project Images -->
      <div
        class="images-section content-section"
        style="--section-index: 4"
        v-if="images && images.length > 0"
      >
        <h3 class="section-title">Galería del Proyecto</h3>
        <div class="images-grid">
          <div
            v-for="(image, index) in images.slice(0, 6)"
            :key="index"
            class="image-item"
            @click="openImageModal(index)"
            :style="{ backgroundImage: 'url(' + getPhotoUrl(image) + ')' }"
          >
            <div class="image-overlay" v-if="index === 5 && images.length > 6">
              <span>+{{ images.length - 6 }}</span>
            </div>
          </div>
        </div>

        <!-- Images Modal -->
        <b-modal
          id="modal-photo2"
          size="xl"
          centered
          hide-header
          body-bg-variant="dark"
          content-class="gallery-modal"
          hide-footer
        >
          <div class="modal-gallery">
            <img
              :src="getPhotoUrl(images[setedPhoto2])"
              alt="Imagen proyecto"
            />
            <div class="modal-nav">
              <button
                class="modal-btn"
                @click="
                  setedPhoto2 =
                    (setedPhoto2 - 1 + images.length) % images.length
                "
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <span class="modal-counter"
                >{{ setedPhoto2 + 1 }} / {{ images.length }}</span
              >
              <button
                class="modal-btn"
                @click="setedPhoto2 = (setedPhoto2 + 1) % images.length"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import BuyProgress from "../../components/BuyProgress.vue";

export default {
  name: "showApartment",
  components: {
    BuyProgress,
  },
  props: {
    asset_id: { default: null },
    photos: { default: () => [] },
    images: { default: () => [] },
    features: { default: () => [] },
  },
  data() {
    return {
      apartment: null,
      carouselPosition: 0,
      setedPhoto: 0,
      setedPhoto2: 0,
      progressAnimated: false,
      progressObserver: null,
      assetDates: {},
      assetDatesLoading: false,
      assetDatesRequestId: 0,
    };
  },
  mounted() {
    this.setApartment();
    this.fetchAssetDates();
    this.setupProgressObserver();
  },
  beforeDestroy() {
    if (this.progressObserver) {
      this.progressObserver.disconnect();
    }
  },
  methods: {
    setupProgressObserver() {
      this.$nextTick(() => {
        if (this.$refs.progressSection) {
          this.progressObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !this.progressAnimated) {
                  // Small delay for smoother effect
                  setTimeout(() => {
                    this.progressAnimated = true;
                  }, 150);
                }
              });
            },
            {
              threshold: 0.3, // Trigger when 30% visible
              rootMargin: "0px",
            }
          );
          this.progressObserver.observe(this.$refs.progressSection);
        }
      });
    },
    setApartment() {
      if (this.asset_id) {
        this.apartment = this.$store.getters.getApartmentById(
          parseInt(this.asset_id)
        );
        console.log(
          "ShowApartmentNew - asset_id:",
          this.asset_id,
          "apartment:",
          this.apartment
        );
      }
    },
    fetchAssetDates() {
      if (!this.canFetchAssetDates) {
        this.assetDatesRequestId += 1;
        this.assetDates = {};
        this.assetDatesLoading = false;
        return;
      }

      const requestId = this.assetDatesRequestId + 1;
      this.assetDatesRequestId = requestId;
      this.assetDatesLoading = true;

      this.axios
        .get(this.assetDataEndpoint(), {
          params: {
            customer_id: this.customerId,
            id: this.asset_id,
          },
        })
        .then((result) => {
          if (requestId !== this.assetDatesRequestId) return;
          this.assetDates = result.data || {};
        })
        .catch(() => {
          if (requestId !== this.assetDatesRequestId) return;
          this.assetDates = {};
        })
        .then(() => {
          if (requestId === this.assetDatesRequestId) {
            this.assetDatesLoading = false;
          }
        });
    },
    assetDataEndpoint() {
      const baseURL = String(
        this.axios?.defaults?.baseURL || process.env.VUE_APP_API_URL || ""
      )
        .replace(/["']/g, "")
        .trim();

      if (!baseURL) return "/api/v1/assets/data";

      if (/\/api\/v1\/customers\/?$/.test(baseURL)) {
        return baseURL.replace(/\/customers\/?$/, "/assets/data");
      }

      return `${baseURL.replace(/\/+$/, "")}/assets/data`;
    },
    getPhotoUrl(photo) {
      if (!photo || !photo.url) return "";
      return photo.url
        .trim()
        .replace(/ /g, "%20")
        .replace(/\(/g, "%28")
        .replace(/\)/g, "%29");
    },
    formatNumber(num) {
      return new Intl.NumberFormat("es-CL").format(num);
    },
    formatDate(value) {
      const parts = String(value || "")
        .slice(0, 10)
        .split("-");

      if (parts.length !== 3) return value;

      const date = new Date(parts[0], Number(parts[1]) - 1, parts[2]);

      return new Intl.DateTimeFormat("es-CL", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(date);
    },
    prevPhoto() {
      this.carouselPosition =
        (this.carouselPosition - 1 + this.photos.length) % this.photos.length;
    },
    nextPhoto() {
      this.carouselPosition = (this.carouselPosition + 1) % this.photos.length;
    },
    showPhoto() {
      this.setedPhoto = this.carouselPosition;
      this.$root.$emit("bv::show::modal", "modal-photo");
    },
    openImageModal(index) {
      this.setedPhoto2 = index;
      this.$root.$emit("bv::show::modal", "modal-photo2");
    },
    hideData(assetType) {
      return [
        "ESTACIONAMIENTO",
        "BODEGA",
        "SITIO",
        "LOTE",
        "BICICLETERO",
        "ACCION",
      ].includes(assetType);
    },
    hasImage(feature) {
      return (
        feature.urlLogoCaracteristica &&
        feature.urlLogoCaracteristica.length > 0 &&
        feature.urlLogoCaracteristica !==
          "https://s3.amazonaws.com/gestion.mobysuite/null"
      );
    },
    setIcon(feature) {
      const iconMap = {
        "Areas Verdes": "leaf",
        "Areas comunes": "dumbbell",
        Infraestructura: "building",
        "Característica departamento": "door-closed",
        "Espacios Utiles": "garage-car",
        Plusvalía: "chart-line",
      };
      return iconMap[feature.categoria] || "building";
    },
    isColorLight(color) {
      if (!color) return false;
      const hex = color.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.8;
    },
  },
  watch: {
    asset_id: {
      handler: function () {
        this.setApartment();
        this.carouselPosition = 0;
        // Reset progress animation for new property
        this.progressAnimated = false;
        this.assetDates = {};
        this.setupProgressObserver();
      },
    },
    assetDatesRequestKey: {
      handler: function () {
        this.fetchAssetDates();
      },
    },
  },
  computed: {
    customer() {
      return this.$store.getters.getCustomer;
    },
    template() {
      return this.$store.getters.getCustomerData?.template || "default";
    },
    country() {
      return this.$store.getters.getCustomerData?.country || 1;
    },
    customerId() {
      return this.$store.getters.getCustomerData?.customer_id || null;
    },
    hasAftersaleProduct() {
      const products = this.$store.getters.getCustomerData?.products || [];
      return products.some((product) => product.name == "PostVenta");
    },
    canFetchAssetDates() {
      return Boolean(
        this.asset_id && this.customerId && this.hasAftersaleProduct
      );
    },
    assetDatesRequestKey() {
      return [
        this.asset_id || "",
        this.customerId || "",
        this.hasAftersaleProduct ? "postventa" : "sin-postventa",
      ].join(":");
    },
    visibleAssetDates() {
      const dates = this.assetDates || {};
      const dateConfig = [
        {
          key: "delivery_date",
          label: "Entrega",
          description: "Fecha registrada para la entrega de tu propiedad.",
        },
        {
          key: "structural_exp_date",
          label: "Garantía estructural",
          description: "Vencimiento asociado a estructura.",
        },
        {
          key: "installation_exp_date",
          label: "Garantía instalaciones",
          description: "Vencimiento asociado a instalaciones.",
        },
        {
          key: "terminations_exp_date",
          label: "Garantía terminaciones",
          description: "Vencimiento asociado a terminaciones.",
        },
      ];

      return dateConfig
        .map((item) => ({
          ...item,
          value: dates[item.key],
        }))
        .filter((item) => item.value);
    },
    isLightBackground() {
      const menuTextColor =
        this.$store.getters.getCustomerData?.color_menu_text;
      return this.isColorLight(menuTextColor);
    },
  },
};
</script>

<style scoped>
.property-detail-wrapper {
  min-height: 200px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6b7280;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--color-btn-a, #3b82f6);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin: 0;
  font-size: 0.95rem;
}

.property-detail {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Fade in animation for main content */
.property-detail.fade-in {
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Section Titles */
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  animation: titleFadeIn 0.4s ease backwards;
}

@keyframes titleFadeIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Gallery Section */
.gallery-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: fadeSlideUp 0.5s ease backwards;
}

@keyframes fadeSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Content Sections Animation */
.content-section {
  animation: sectionFadeIn 0.4s ease backwards;
  animation-delay: calc(var(--section-index, 0) * 0.15s + 0.3s);
}

@keyframes sectionFadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.gallery-main {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
}

.gallery-image {
  width: 100%;
  height: 320px;
  background-size: cover;
  background-position: center;
}

.gallery-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    transparent 50%,
    rgba(0, 0, 0, 0.4) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
}

.gallery-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.gallery-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #1f2937;
}

.gallery-btn:hover {
  background: white;
  transform: scale(1.1);
}

.gallery-counter {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.gallery-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  color: white;
  font-size: 0.85rem;
  opacity: 0.9;
}

.gallery-thumbs {
  display: flex;
  gap: 8px;
}

.thumb-btn {
  width: 72px;
  height: 54px;
  border-radius: 8px;
  border: 2px solid transparent;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  animation: thumbSlideIn 0.3s ease backwards;
}

.thumb-btn:nth-child(1) {
  animation-delay: 0.3s;
}
.thumb-btn:nth-child(2) {
  animation-delay: 0.35s;
}
.thumb-btn:nth-child(3) {
  animation-delay: 0.4s;
}
.thumb-btn:nth-child(4) {
  animation-delay: 0.45s;
}
.thumb-btn:nth-child(5) {
  animation-delay: 0.5s;
}

@keyframes thumbSlideIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.thumb-btn:hover {
  border-color: #9ca3af;
}

.thumb-btn.active {
  border-color: var(--color-btn-a, #3b82f6);
}

.thumb-more {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

/* Stats Section */
.stats-section {
  padding: 0;
  animation: sectionFadeIn 0.4s ease backwards;
  animation-delay: 0.15s;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  animation: cardSlideIn 0.4s ease backwards;
  animation-delay: calc(var(--card-index, 0) * 0.1s);
}

@keyframes cardSlideIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card:hover {
  border-color: #d1d5db;
  background: #f3f4f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-btn-a, #3b82f6);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  color: #6b7280;
}

/* Areas Section */
.areas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.area-card {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  text-align: center;
  transition: all 0.2s ease;
  animation: cardSlideIn 0.4s ease backwards;
}

.area-card:nth-child(1) {
  animation-delay: 0.2s;
}
.area-card:nth-child(2) {
  animation-delay: 0.3s;
}
.area-card:nth-child(3) {
  animation-delay: 0.4s;
}
.area-card:nth-child(4) {
  animation-delay: 0.5s;
}
.area-card:nth-child(5) {
  animation-delay: 0.6s;
}

.area-card:hover {
  border-color: #d1d5db;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.area-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
}

.area-label {
  font-size: 0.8rem;
  color: #6b7280;
}

/* Key Dates Section */
.dates-panel {
  position: relative;
  overflow: hidden;
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.dates-panel::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    var(--color-a, #3b82f6),
    var(--color-btn-a, #2563eb)
  );
}

.dates-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.dates-header .section-title {
  margin: 2px 0 0;
}

.dates-eyebrow {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-a, #3b82f6);
  text-transform: uppercase;
}

.dates-calendar-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.1);
  color: var(--color-a, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dates-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.date-row {
  position: relative;
  display: flex;
  gap: 12px;
  min-height: 112px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s ease;
  animation: dateCardIn 0.35s ease backwards;
  animation-delay: calc(var(--date-index, 0) * 0.08s);
}

.date-row:hover {
  border-color: #d1d5db;
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.date-row.main {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.24);
}

.date-marker {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #dbe3ec;
  color: var(--color-a, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.date-marker span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.date-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.date-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #4b5563;
  margin-bottom: 4px;
}

.date-content strong {
  color: #111827;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.25;
}

.date-content small {
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.35;
  margin-top: 7px;
}

.dates-skeleton-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.dates-skeleton-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-height: 104px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: rgba(255, 255, 255, 0.75);
}

.dates-skeleton-dot,
.dates-skeleton-copy span,
.dates-skeleton-copy strong {
  display: block;
  background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%);
  background-size: 200% 100%;
  animation: dateSkeleton 1.2s ease-in-out infinite;
}

.dates-skeleton-dot {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dates-skeleton-copy {
  width: 100%;
  padding-top: 2px;
}

.dates-skeleton-copy span {
  width: 42%;
  height: 11px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.dates-skeleton-copy strong {
  width: 72%;
  height: 18px;
  border-radius: 10px;
}

@keyframes dateCardIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes dateSkeleton {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

/* Progress Section */
.progress-section {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-header .section-title {
  margin: 0;
}

.progress-percent {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-a, #3b82f6);
}

.progress-bar-container {
  height: 10px;
  background: #e5e7eb;
  border-radius: 5px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: var(--color-a, #3b82f6);
  border-radius: 5px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Progress animation on scroll */
.progress-section .progress-header {
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.4s ease;
}

.progress-section.animate-in .progress-header {
  opacity: 1;
  transform: translateY(0);
}

.progress-section .progress-percent {
  transition: all 0.3s ease;
}

/* Inverted colors for light backgrounds */
.progress-section.inverted-colors .progress-percent {
  color: var(--color-a, #3b82f6);
}

.progress-section.inverted-colors .progress-bar-fill {
  background: var(--color-a, #3b82f6);
}

/* Features Section */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.feature-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  animation: cardSlideIn 0.4s ease backwards;
}

.feature-card:nth-child(1) {
  animation-delay: 0.3s;
}
.feature-card:nth-child(2) {
  animation-delay: 0.35s;
}
.feature-card:nth-child(3) {
  animation-delay: 0.4s;
}
.feature-card:nth-child(4) {
  animation-delay: 0.45s;
}
.feature-card:nth-child(5) {
  animation-delay: 0.5s;
}
.feature-card:nth-child(6) {
  animation-delay: 0.55s;
}
.feature-card:nth-child(7) {
  animation-delay: 0.6s;
}
.feature-card:nth-child(8) {
  animation-delay: 0.65s;
}

.feature-card:hover {
  border-color: #d1d5db;
  background: #f3f4f6;
}

.feature-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.feature-fa-icon {
  font-size: 1.25rem;
  color: var(--color-btn-a, #3b82f6);
}

.feature-text {
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.3;
}

/* Images Section */
.images-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.image-item {
  aspect-ratio: 4/3;
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease;
  animation: imageScaleIn 0.4s ease backwards;
}

.image-item:nth-child(1) {
  animation-delay: 0.5s;
}
.image-item:nth-child(2) {
  animation-delay: 0.55s;
}
.image-item:nth-child(3) {
  animation-delay: 0.6s;
}
.image-item:nth-child(4) {
  animation-delay: 0.65s;
}
.image-item:nth-child(5) {
  animation-delay: 0.7s;
}
.image-item:nth-child(6) {
  animation-delay: 0.75s;
}

@keyframes imageScaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.image-item:hover {
  transform: scale(1.02);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
}

/* Modal Styles */
.gallery-modal {
  background: transparent !important;
  border: none !important;
}

.modal-gallery {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.modal-gallery img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
}

.modal-nav {
  display: flex;
  align-items: center;
  gap: 24px;
}

.modal-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-counter {
  color: white;
  font-size: 1rem;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .property-detail {
    gap: 20px;
  }

  .section-title {
    font-size: 1rem;
    margin-bottom: 12px;
  }

  .gallery-image {
    height: 220px;
  }

  .gallery-btn {
    width: 36px;
    height: 36px;
  }

  .gallery-counter {
    padding: 5px 10px;
    font-size: 0.8rem;
  }

  .gallery-hint {
    font-size: 0.75rem;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .stat-card {
    padding: 14px;
    gap: 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
  }

  .stat-value {
    font-size: 1.35rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .areas-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .area-card {
    padding: 16px 12px;
  }

  .area-value {
    font-size: 1.1rem;
  }

  .area-label {
    font-size: 0.75rem;
  }

  .dates-panel {
    padding: 18px;
  }

  .dates-list,
  .dates-skeleton-list {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .date-row,
  .dates-skeleton-item {
    min-height: 96px;
  }

  .progress-section {
    padding: 16px;
  }

  .progress-percent {
    font-size: 1.1rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .feature-card {
    padding: 12px 14px;
  }

  .feature-icon {
    width: 32px;
    height: 32px;
  }

  .feature-text {
    font-size: 0.85rem;
  }

  .images-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .image-item {
    aspect-ratio: 1/1;
  }

  .modal-gallery img {
    max-height: 60vh;
  }

  .modal-nav {
    gap: 16px;
  }

  .modal-btn {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 576px) {
  .property-detail {
    gap: 16px;
  }

  .gallery-section {
    gap: 8px;
  }

  .gallery-main {
    border-radius: 12px;
  }

  .gallery-image {
    height: 200px;
  }

  .gallery-thumbs {
    display: none;
  }

  .gallery-overlay {
    padding: 12px;
  }

  .gallery-btn {
    width: 32px;
    height: 32px;
  }

  .gallery-btn svg {
    width: 16px;
    height: 16px;
  }

  .gallery-counter {
    top: 12px;
    right: 12px;
    padding: 4px 8px;
    font-size: 0.75rem;
  }

  .gallery-hint {
    display: none;
  }

  .stats-section {
    padding: 0;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .stat-card {
    padding: 12px;
    gap: 10px;
    border-radius: 10px;
  }

  .stat-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }

  .stat-icon svg {
    width: 20px;
    height: 20px;
  }

  .stat-value {
    font-size: 1.2rem;
  }

  .stat-label {
    font-size: 0.7rem;
  }

  .areas-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .area-card {
    padding: 14px 10px;
    border-radius: 10px;
  }

  .area-value {
    font-size: 1rem;
    margin-bottom: 2px;
  }

  .area-label {
    font-size: 0.7rem;
  }

  .dates-panel {
    padding: 16px 14px;
    border-radius: 10px;
  }

  .dates-header {
    margin-bottom: 14px;
  }

  .dates-calendar-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
  }

  .dates-eyebrow {
    font-size: 0.68rem;
  }

  .date-row {
    padding: 12px;
    border-radius: 10px;
    min-height: 92px;
  }

  .date-marker,
  .dates-skeleton-dot {
    width: 30px;
    height: 30px;
  }

  .date-label {
    font-size: 0.73rem;
  }

  .date-content strong {
    font-size: 0.95rem;
  }

  .date-content small {
    font-size: 0.72rem;
    margin-top: 5px;
  }

  .progress-section {
    padding: 14px;
    border-radius: 10px;
  }

  .progress-header {
    margin-bottom: 10px;
  }

  .progress-header .section-title {
    font-size: 0.95rem;
  }

  .progress-percent {
    font-size: 1rem;
  }

  .progress-bar-container {
    height: 8px;
  }

  .features-grid {
    gap: 8px;
  }

  .feature-card {
    padding: 10px 12px;
    border-radius: 8px;
    gap: 10px;
  }

  .feature-icon {
    width: 28px;
    height: 28px;
  }

  .feature-fa-icon {
    font-size: 1rem;
  }

  .feature-text {
    font-size: 0.8rem;
  }

  .images-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .image-item {
    border-radius: 10px;
  }

  .image-overlay {
    font-size: 1rem;
  }

  .modal-gallery img {
    max-height: 50vh;
    border-radius: 6px;
  }

  .modal-nav {
    gap: 12px;
  }

  .modal-btn {
    width: 36px;
    height: 36px;
  }

  .modal-counter {
    font-size: 0.9rem;
  }
}

@media (max-width: 375px) {
  .property-detail {
    gap: 14px;
  }

  .gallery-image {
    height: 180px;
  }

  .section-title {
    font-size: 0.95rem;
  }

  .stats-grid {
    gap: 6px;
  }

  .stat-card {
    padding: 10px;
    gap: 8px;
  }

  .stat-icon {
    width: 32px;
    height: 32px;
  }

  .stat-value {
    font-size: 1.1rem;
  }

  .areas-grid {
    gap: 6px;
  }

  .area-card {
    padding: 12px 8px;
  }

  .area-value {
    font-size: 0.95rem;
  }

  .images-grid {
    gap: 6px;
  }
}
</style>
